import React, { useState, useEffect } from 'react';
import { createBillingReport, getTeamsForBilling, createXlsxReport, getAgenturenByOrganization } from '../../api';

function BillingReportDialog({ isOpen, onClose, user, organizationId }) {
  const [reportType, setReportType] = useState('projects'); // 'projects' oder 'agenturen'
  const [teams, setTeams] = useState([]);
  const [agenturen, setAgenturen] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedAgentur, setSelectedAgentur] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [format, setFormat] = useState('csv');
  const [applySonderregelung, setApplySonderregelung] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Teams und Agenturen laden
  useEffect(() => {
    if (isOpen && organizationId) {
      if (reportType === 'projects') {
        fetchTeams();
      } else if (reportType === 'agenturen') {
        fetchAgenturen();
      }
    }
  }, [isOpen, organizationId, reportType]);

  const fetchTeams = async () => {
    try {
      console.log('fetchTeams - Starte mit:', { userId: user.id, organizationId });
      
      const data = await getTeamsForBilling(user.id, organizationId);
      console.log('fetchTeams - Antwort erhalten:', data);
      
      if (data.success) {
        console.log('fetchTeams - Teams gefunden:', data.teams);
        setTeams(data.teams || []);
      } else {
        console.error('fetchTeams - Fehler:', data.error);
        setError('Fehler beim Laden der Teams: ' + data.error);
      }
    } catch (err) {
      console.error('fetchTeams - Netzwerkfehler:', err);
      setError('Netzwerkfehler beim Laden der Teams: ' + err.message);
    }
  };

  const fetchAgenturen = async () => {
    try {
      console.log('fetchAgenturen - Starte mit organizationId:', organizationId);
      
      const data = await getAgenturenByOrganization(organizationId);
      console.log('fetchAgenturen - Antwort erhalten:', data);
      
      if (data.success) {
        console.log('fetchAgenturen - Agenturen gefunden:', data.agenturen);
        setAgenturen(data.agenturen || []);
      } else {
        console.error('fetchAgenturen - Fehler:', data.error);
        setError('Fehler beim Laden der Agenturen: ' + data.error);
      }
    } catch (err) {
      console.error('fetchAgenturen - Netzwerkfehler:', err);
      setError('Netzwerkfehler beim Laden der Agenturen: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (reportType === 'projects' && !selectedTeam) {
      setError('Bitte wählen Sie ein Team aus.');
      return;
    }
    
    if (reportType === 'agenturen' && !selectedAgentur) {
      setError('Bitte wählen Sie eine Agentur aus.');
      return;
    }
    
    if (!dateFrom || !dateTo) {
      setError('Bitte wählen Sie einen Zeitraum aus.');
      return;
    }
    
    if (new Date(dateFrom) > new Date(dateTo)) {
      setError('Das Startdatum darf nicht nach dem Enddatum liegen.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log('BillingReportDialog - Übergebe Parameter:', {
        userId: user.id,
        reportType,
        teamId: selectedTeam,
        agenturId: selectedAgentur,
        dateFrom,
        dateTo,
        format,
        applySonderregelung
      });
      
      const response = await createBillingReport(
        user.id,
        reportType === 'projects' ? selectedTeam : null,
        dateFrom,
        dateTo,
        format,
        applySonderregelung,
        reportType === 'agenturen' ? selectedAgentur : null
      );

      if (response.success) {
        setSuccess('Abrechnung wurde erfolgreich erstellt!');
        if (format === 'xlsx') {
          // XLSX-Datei erstellen
          if (response.data || response.dataSpecial) {
            const fileName = `abrechnung_${response.summary.team}_${dateFrom}_${dateTo}.xlsx`;
            const xlsxResult = await createXlsxReport(response.data, fileName, response.dataSpecial);
            
            if (!xlsxResult.success) {
              setError('Fehler beim Erstellen der XLSX-Datei: ' + xlsxResult.error);
            }
          }
        } else {
          // CSV-Datei herunterladen
          if (response.csvData) {
            const blob = new Blob([response.csvData], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `abrechnung_${selectedTeam}_${dateFrom}_${dateTo}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        }
        
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      } else {
        setError('Fehler beim Erstellen der Abrechnung: ' + response.error);
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReportType('projects');
    setSelectedTeam('');
    setSelectedAgentur('');
    setDateFrom('');
    setDateTo('');
    setFormat('csv');
    setApplySonderregelung(true);
    setError('');
    setSuccess('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content billing-modal">
        <div className="modal-header">
          <h2>📊 Abrechnung erstellen</h2>
          <button className="close-button" onClick={handleClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="billing-form">
          {error && (
            <div className="error-message">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22,4 12,14.01 9,11.01"></polyline>
              </svg>
              {success}
            </div>
          )}

          <div className="form-group">
            <label>Abrechnungstyp:</label>
            <div className="report-type-options">
              <label className="report-type-option">
                <input
                  type="radio"
                  name="reportType"
                  value="projects"
                  checked={reportType === 'projects'}
                  onChange={(e) => {
                    setReportType(e.target.value);
                    setSelectedTeam('');
                    setSelectedAgentur('');
                  }}
                />
                <span className="report-type-label">Nach Projekten</span>
              </label>
              <label className="report-type-option">
                <input
                  type="radio"
                  name="reportType"
                  value="agenturen"
                  checked={reportType === 'agenturen'}
                  onChange={(e) => {
                    setReportType(e.target.value);
                    setSelectedTeam('');
                    setSelectedAgentur('');
                  }}
                />
                <span className="report-type-label">Nach Agenturen</span>
              </label>
            </div>
          </div>

          {reportType === 'projects' && (
            <div className="form-group">
              <label htmlFor="team">Projekt/Team auswählen:</label>
              <select
                id="team"
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                required
              >
                <option value="">Projekt auswählen...</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {reportType === 'agenturen' && (
            <div className="form-group">
              <label htmlFor="agentur">Agentur auswählen:</label>
              <select
                id="agentur"
                value={selectedAgentur}
                onChange={(e) => setSelectedAgentur(e.target.value)}
                required
              >
                <option value="">Agentur auswählen...</option>
                {agenturen.map(agentur => (
                  <option key={agentur.id} value={agentur.id}>
                    {agentur.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dateFrom">Zeitraum von:</label>
              <input
                type="date"
                id="dateFrom"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="dateTo">Zeitraum bis:</label>
              <input
                type="date"
                id="dateTo"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Format auswählen:</label>
            <div className="format-options">
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="csv"
                  checked={format === 'csv'}
                  onChange={(e) => setFormat(e.target.value)}
                />
                <span className="format-label">CSV</span>
              </label>
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="xlsx"
                  checked={format === 'xlsx'}
                  onChange={(e) => setFormat(e.target.value)}
                />
                <span className="format-label">XLSX</span>
              </label>
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="pdf"
                  checked={format === 'pdf'}
                  onChange={(e) => setFormat(e.target.value)}
                  disabled
                />
                <span className="format-label disabled">PDF (bald verfügbar)</span>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={applySonderregelung}
                onChange={(e) => setApplySonderregelung(e.target.checked)}
              />
              <span>Sonderregelung anwenden (Leads mit mehr als 4 Wochen Zeitdifferenz separat aufführen)</span>
            </label>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={handleClose}
              disabled={loading}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 11-6.219-8.56"></path>
                  </svg>
                  Erstelle Abrechnung...
                </>
              ) : (
                'Abrechnung erstellen'
              )}
            </button>
          </div>
        </form>

        <style jsx>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }

          .billing-modal {
            background: white;
            border-radius: 12px;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
            width: 90%;
            max-width: 600px;
            max-height: 90vh;
            overflow-y: auto;
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 24px 24px 0 24px;
            border-bottom: 1px solid #e2e8f0;
            margin-bottom: 24px;
          }

          .modal-header h2 {
            margin: 0;
            color: #2d3748;
            font-size: 1.5rem;
            font-weight: 600;
          }

          .close-button {
            background: none;
            border: none;
            cursor: pointer;
            padding: 8px;
            border-radius: 6px;
            color: #718096;
            transition: all 0.2s;
          }

          .close-button:hover {
            background-color: #f7fafc;
            color: #4a5568;
          }

          .billing-form {
            padding: 0 24px 24px 24px;
          }

          .form-group {
            margin-bottom: 20px;
          }

          .form-row {
            display: flex;
            gap: 16px;
          }

          .form-row .form-group {
            flex: 1;
          }

          .form-group label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: #4a5568;
            font-size: 0.875rem;
          }

          .form-group select,
          .form-group input[type="date"] {
            width: 100%;
            padding: 12px;
            border: 2px solid #e2e8f0;
            border-radius: 8px;
            font-size: 0.875rem;
            transition: border-color 0.2s, box-shadow 0.2s;
          }

          .form-group select:focus,
          .form-group input[type="date"]:focus {
            outline: none;
            border-color: #4a6de5;
            box-shadow: 0 0 0 3px rgba(74, 109, 229, 0.1);
          }

          .report-type-options,
          .format-options {
            display: flex;
            gap: 16px;
            flex-wrap: wrap;
          }

          .report-type-option,
          .format-option {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            padding: 12px 16px;
            border: 2px solid #e2e8f0;
            border-radius: 8px;
            transition: all 0.2s;
          }

          .report-type-option:hover,
          .format-option:hover {
            border-color: #4a6de5;
            background-color: #f8fafc;
          }

          .report-type-option input[type="radio"],
          .format-option input[type="radio"] {
            margin: 0;
          }

          .report-type-option input[type="radio"]:checked + .report-type-label,
          .format-option input[type="radio"]:checked + .format-label {
            color: #4a6de5;
            font-weight: 600;
          }

          .report-type-label,
          .format-label {
            font-size: 0.875rem;
            color: #4a5568;
          }

          .format-label.disabled {
            color: #a0aec0;
            cursor: not-allowed;
          }

          .checkbox-label {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            cursor: pointer;
          }

          .checkbox-label input[type="checkbox"] {
            margin-top: 3px;
            width: 18px;
            height: 18px;
            cursor: pointer;
          }

          .checkbox-label span {
            font-size: 0.875rem;
            color: #4a5568;
            line-height: 1.5;
          }

          .form-actions {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
            margin-top: 32px;
            padding-top: 24px;
            border-top: 1px solid #e2e8f0;
          }

          .cancel-button,
          .submit-button {
            padding: 12px 24px;
            border-radius: 8px;
            font-size: 0.875rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            border: none;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .cancel-button {
            background-color: #f7fafc;
            color: #4a5568;
            border: 1px solid #e2e8f0;
          }

          .cancel-button:hover:not(:disabled) {
            background-color: #edf2f7;
            color: #2d3748;
          }

          .submit-button {
            background-color: #4a6de5;
            color: white;
          }

          .submit-button:hover:not(:disabled) {
            background-color: #3c5ce0;
          }

          .submit-button:disabled,
          .cancel-button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .loading-spinner {
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }

          .error-message,
          .success-message {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 16px;
            border-radius: 8px;
            margin-bottom: 20px;
            font-size: 0.875rem;
          }

          .error-message {
            background-color: #fed7d7;
            color: #c53030;
            border: 1px solid #feb2b2;
          }

          .success-message {
            background-color: #c6f6d5;
            color: #2f855a;
            border: 1px solid #9ae6b4;
          }

          @media (max-width: 768px) {
            .billing-modal {
              width: 95%;
              margin: 20px;
            }

            .form-row {
              flex-direction: column;
              gap: 0;
            }

            .format-options {
              flex-direction: column;
              gap: 8px;
            }

            .form-actions {
              flex-direction: column;
            }
          }
        `}</style>
      </div>
    </div>
  );
}

export default BillingReportDialog; 